import { CollabSchema } from '@common/prosemirror/model/collab-schema';
import { depthAtStartOfFragment, depthInFirstChildOfFragment } from '@common/prosemirror/model/fragment';
import { getNearestAncestorRange, getTextRanges } from '@common/prosemirror/model/range';
import { FoundResolvedPosInfo, resolvedPosFind } from '@common/prosemirror/model/resolved-pos';
import { getTrackedChangesState, TrackedChangesIgnoreMetaKey, TrackedChangesTrackedMetaKey } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { copyNodeWithChanges, copySliceWithChanges } from '@common/prosemirror/tracked-changes/copy';
import { TrackedChangeNodeAttrs } from '@common/prosemirror/tracked-changes/tracked-change-node-attrs.type';
import { getTrackedChangeNodeAttrs, removeTrackedChangesFromNodeAttrs } from '@common/prosemirror/tracked-changes/tracked-changes';
import { liftTargetFromNodeType } from '@common/prosemirror/transform/node';
import { Fragment, NodeRange, NodeType, ProseMirrorNode, ResolvedPos, Slice } from 'prosemirror-model';
import { AllSelection, Command, EditorState, TextSelection } from 'prosemirror-state';
import { ReplaceAroundStep, Transform } from 'prosemirror-transform';

export interface LiftFromInlineNodeOptions {
  liftAncestorForEmptySelection?: boolean;
  skipTrackedChanges?: boolean;
}

export function getNodeRangesAndTargetsForLiftFromInlineNode(state: EditorState, nodeType: NodeType, options: LiftFromInlineNodeOptions = {}): { range: NodeRange, target: number }[] {
  // Calculate the ranges to lift
  let ranges: NodeRange[] = [];

  if (state.selection.empty || options.liftAncestorForEmptySelection) {
    const textSelection = state.selection as TextSelection;
    const range = getNearestAncestorRange(textSelection.$cursor || textSelection.$from, nodeType);
    if (range) {
      ranges.push(range);
    }
  } else {
    ranges = getTextRanges(state.selection.from, state.selection.to, state.doc, nodeType) ?? [];
  }

  return ranges.map(range => {
    const target = liftTargetFromNodeType(range, nodeType);

    if (typeof target === 'number') {
      return { range, target };
    }
  }).filter(item => !!item);
}

/**
 * Lifts the content in the given range from an ancestor node.
 * You'll probably want to use getNodeRangesAndTargetsForLiftFromInlineNode to compute target, to make sure the lift is valid.
 * @param tr The transaction to apply the lift to.
 * @param range The range of the content to lift.
 * @param target The depth of the content to lift.
 * @returns The new from and to positions of the lifted content.
 */
export function liftInlineNode(tr: Transform, range: NodeRange, target: number): { from: number, to: number } {
  // Adjust positions to other transactions
  const $from = tr.doc.resolve(tr.mapping.map(range.$from.pos));
  const $to = tr.doc.resolve(tr.mapping.map(range.$to.pos));
  const depth = range.depth;
  const gapStart = $from.before(depth + 1);
  const gapEnd = $to.after(depth + 1);
  let start = gapStart;
  let end = gapEnd;

  // Build the slice that will wrap around the range
  let insert = Fragment.empty;
  let before = Fragment.empty,

  openStart = 0;
  for (let d = depth; d > target; d -= 1) {
    const $pos = tr.doc.resolve($from.pos - depth + d);
    // If the from position is at the start of the parent or the position is at the start of the parent at the current depth
    if (d > target && ($from.parentOffset > 0 || $pos.parentOffset > 0)) {
      before = Fragment.from($from.node(d).copy(before));
      openStart += 1;
    }

    if (d > target + 1) {
      insert = Fragment.from($from.node(d).copy(insert));
    }
  }

  let after = Fragment.empty,
  openEnd = 0;
  for (let d = depth; d > target; d -= 1) {
    const $pos = tr.doc.resolve($to.pos + depth - d);
    // If the to position is at the end of the parent or the position is at the end of the parent at the current depth
    if (d > target && ($to.pos < $to.end() || $pos.pos < $pos.end())) {
      after = Fragment.from($to.node(d).copy(after));
      openEnd += 1;
    }
  }

  // If at the beginning of the inline node then move the start back to the beginning of the target node so that the target node is removed at the start plus any additional added open start from the slice
  if ($from.parentOffset === 0) {
    start -= (depth - target - openStart);
  }

  // If at the end of the inline node then move the end position to the end of the target node so that the target node is removed plus any additional added open end from the slice
  if ($to.pos >= $to.end()) {
    end += (depth - target - openEnd);
  }

  // Make the slice
  const slice = new Slice(before.append(insert).append(after), openStart, openEnd);

  // Make the transformation
  tr.step(new ReplaceAroundStep(start, end, gapStart, gapEnd, slice, openStart + depthAtStartOfFragment(insert), true));

  return {
    // The beginning where the slice is inserted
    from: start + openStart,
    // The slice and the content preserved but before the end
    to: start + (slice.size + (gapEnd - gapStart)) - openEnd
  };
}

/**
 * Lifts the content from an inline node.
 * @param nodeType The node type to lift from.
 * @param options The options for the command.
 * @returns The command.
 */
export function liftFromInlineNode(nodeType: NodeType, options: LiftFromInlineNodeOptions = {}): Command {
  return function (state: EditorState, dispatch?: ProsemirrorDispatcher): boolean {
    if (state.selection.empty && !options.liftAncestorForEmptySelection) {
      return false;
    }

    // Get the ranges to lift
    const rangesAndTargets = getNodeRangesAndTargetsForLiftFromInlineNode(state, nodeType, options);

    // If there are no ranges then return false indicating the command cannot be run
    if (rangesAndTargets.length === 0) {
      return false;
    }

    if (dispatch) {
      const trackedChangesState = getTrackedChangesState(state);

      // Create the transaction
      const tr = state.tr;

      // The ranges to lift as slices. Use slice instead of node range because the range may cross nodes
      let slices: { targetNode: FoundResolvedPosInfo, $from: ResolvedPos, $to: ResolvedPos }[] = [];

      // Reduce the ranges and targets when they are adjacent to each other
      rangesAndTargets.forEach(r => {
        const targetNode: FoundResolvedPosInfo = resolvedPosFind(r.range.$from, n => n.type === nodeType);
        const slice = slices.find(slice => slice.targetNode.$nodePos.pos === targetNode.$nodePos.pos);

        if (slice) {
          // Increase the max range
          slice.$to = r.range.$to;
        } else {
          // Add a new slice
          slices.push({ targetNode: targetNode, $from: r.range.$from, $to: r.range.$to });
        }
      });

      // Do in reverse to not worry about mapping
      slices.reverse().forEach(rangeAndTarget => {
        const sliceSize: number = rangeAndTarget.$to.pos - rangeAndTarget.$from.pos;

        let $from: ResolvedPos = rangeAndTarget.$from;
        // Move the position to cross the node when it is at the start of the node
        while ($from.parentOffset === 0 && $from.parent.type !== nodeType) {
          $from = tr.doc.resolve($from.before());
        }

        let $to: ResolvedPos = rangeAndTarget.$to;
        // Move the position to cross the node when it is at the end of the node
        while ($to.end() === $to.pos && $to.parent.type !== nodeType) {
          $to = tr.doc.resolve($to.after());
        }

        // Shortcut names
        const from: number = $from.pos;
        const to: number = $to.pos;
        const targetNode: FoundResolvedPosInfo = rangeAndTarget.targetNode;

        let selectionSlice: Slice = tr.doc.slice(from, to);
        // If the range is a simple text selection, do an unbound
        if ($from.depth === targetNode.depth && $to.depth === targetNode.depth && selectionSlice.content.childCount === 1 && selectionSlice.content.firstChild.isText) {
          const newRange = liftInlineNode(tr, new NodeRange($from, $to, $from.depth), targetNode.$nodePos.depth);
          if (trackedChangesState?.enabled && !options.skipTrackedChanges) {
            let unboundAttr: TrackedChangeNodeAttrs;
            let ignoreUnbindTrack: boolean = false;

            // Grab the existing tracked changes on the node
            let trackedChangeNodeAttrs: TrackedChangeNodeAttrs = getTrackedChangeNodeAttrs(trackedChangesState.changeMarkType, targetNode.node);
            ignoreUnbindTrack = trackedChangeNodeAttrs?.changeList?.some(change => change.changeType === ChangeType.Bind);

            // The changeContent should not include the node's content or tracked changes so remove them and then serialize the node to XML
            const unboundNodeXML: string = (state.schema as CollabSchema).nodeToCode(nodeType.create(removeTrackedChangesFromNodeAttrs(targetNode.node.attrs), null, targetNode.node.marks));
            unboundAttr = trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Unbind, { changeContent: unboundNodeXML });
            if (!ignoreUnbindTrack && unboundAttr) {
              tr.addMark(newRange.from, newRange.to, trackedChangesState.changeMarkType.create(unboundAttr));
            }
          }
        } else {
          const targetNodePos: number = targetNode.$nodePos.pos;
          const targetNodeEnd: number = targetNode.$nodePos.pos + targetNode.node.nodeSize;

          // Replace the range with the unwrapped version
          let openStart: number = 0;
          let before: Fragment = Fragment.empty;
          const isAtStart: boolean = from === targetNodePos + 1;
          if (!isAtStart) {
            // Create the open start nodes of the fragment
            for (let d = $from.depth; d > targetNode.$nodePos.depth; d -= 1) {
              before = Fragment.from($from.node(d).copy(before));
              openStart += 1;
            }
          }

          let after = Fragment.empty;
          let openEnd: number = 0;
          const isAtEnd: boolean = to === targetNodeEnd - 1;
          // Create the open end nodes of the fragment
          if (!isAtEnd) {
            for (let d = $to.depth; d > targetNode.$nodePos.depth; d -= 1) {
              after = Fragment.from($to.node(d).copy(after));
              openEnd += 1;
            }
          }

          let insert: Fragment = selectionSlice.content;
          const $shallowerPos: ResolvedPos = $from.depth < $to.depth ? $from : $to;
          // Create the middle content of the fragment
          for (let d = $shallowerPos.depth; d > targetNode.depth; d--) {
            insert = Fragment.from($shallowerPos.node(d).copy(insert));
          }

          const newSlice: Slice = new Slice(before.append(insert).append(after), openStart, openEnd);
          tr.replace(isAtStart ? targetNodePos : from, isAtEnd ? targetNodeEnd : to, newSlice);

          const newFrom: number = (isAtStart ? targetNodePos : from) + openStart + (depthInFirstChildOfFragment(insert) + (insert.firstChild.isLeaf ? 0 : 1)) /** Adjust to point into the text depth */;
          if (trackedChangesState?.enabled && !options.skipTrackedChanges) {
            // Replace the new node type with a tracked add copy
            const mappedFrom: number = tr.mapping.map(targetNodePos);
            const mappedTo: number = tr.mapping.map(targetNodeEnd);
            const newTargetNodes: Slice = tr.doc.slice(mappedFrom, mappedTo);
            const trackedAddTarget: Slice = copySliceWithChanges(trackedChangesState?.changeMarkType, newTargetNodes, trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Add));
            tr.replaceRange(mappedFrom, mappedTo, trackedAddTarget);
            // Set the selection to the same range
            tr.setSelection(TextSelection.between(tr.doc.resolve(newFrom), tr.doc.resolve(newFrom + sliceSize)));
            // Insert a tracked delete of the old node type
            const oldTrackedDelete: ProseMirrorNode = copyNodeWithChanges(trackedChangesState?.changeMarkType, targetNode.node, trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Remove));
            tr.insert(mappedFrom, oldTrackedDelete);
          } else {
            // Set the selection to the same range
            tr.setSelection(TextSelection.between(tr.doc.resolve(newFrom), tr.doc.resolve(newFrom + sliceSize)));
          }
        }
      });

      if (state.selection instanceof AllSelection) {
        tr.setSelection(new AllSelection(tr.doc));
      }

      // Mark that the changes were tracked
      if (trackedChangesState?.enabled) {
        if (options.skipTrackedChanges) {
          tr.setMeta(TrackedChangesIgnoreMetaKey, true);
        } else {
          tr.setMeta(TrackedChangesTrackedMetaKey, true);
        }
      }

      // Dispatch the transaction
      dispatch(tr.scrollIntoView());
    }

    return true;
  };
}
